import React from 'react';
import { Link } from 'react-router-dom';
import * as LucideIcons from 'lucide-react';
import { ArrowRight, ArrowUpRight } from 'lucide-react';
import { activities } from '../../data/mock';
import useReveal from '../../hooks/useReveal';

/**
 * All eleven learning modules. Each card carries the module's own accent colour
 * on a hairline that grows in on hover - enough personality to differentiate
 * them without turning the grid into a colour swatch.
 */
const ActivitiesSection = () => {
  const ref = useReveal({ stagger: 55 });

  return (
    <section ref={ref} className="section">
      <header className="section-header">
        <p className="eyebrow reveal">The modules</p>
        <h2 className="heading-2 reveal mt-3">Eleven ways to get better</h2>
        <p className="body-large reveal">
          Speaking, writing, reading, grammar. Every module is scored by AI the moment you
          finish, with the reasoning behind the mark.
        </p>
      </header>

      <div className="grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-3">
        {activities.map((activity) => {
          const Icon = LucideIcons[activity.icon] || LucideIcons.Circle;

          return (
            <Link
              key={activity.id}
              to={`/activities/${activity.id}`}
              className="module-card reveal group flex flex-col"
              style={{
                '--card-accent': activity.color,
                '--card-wash': `${activity.color}17`
              }}
            >
              <div className="flex items-start justify-between">
                <span className="module-icon">
                  <Icon className="h-5 w-5" />
                </span>
                <ArrowUpRight
                  className="h-4 w-4 -translate-y-1 opacity-0 transition-all duration-300 group-hover:translate-y-0 group-hover:opacity-100"
                  style={{ color: activity.color }}
                  aria-hidden="true"
                />
              </div>

              <h3
                className="heading-3 transition-colors"
                style={{ fontSize: '1.05rem' }}
              >
                {activity.name}
              </h3>
              <p className="body-medium mt-2 flex-1 text-[0.9rem]">{activity.description}</p>

              <div className="mt-4 flex flex-wrap gap-1.5">
                {activity.skills.map((skill) => (
                  <span
                    key={skill}
                    className="rounded-full px-2.5 py-1 text-[0.72rem] font-medium"
                    style={{
                      background: 'rgba(14,21,34,0.045)',
                      color: 'var(--text-secondary)'
                    }}
                  >
                    {skill}
                  </span>
                ))}
              </div>
            </Link>
          );
        })}
      </div>

      <div className="reveal mt-12 text-center">
        <Link to="/activities" className="btn-secondary">
          Explore every module
          <ArrowRight className="h-4 w-4" />
        </Link>
      </div>
    </section>
  );
};

export default ActivitiesSection;
